import React, { Dispatch, SetStateAction, useId } from 'react';
import dayjs from 'dayjs';
import { ChevronLeft, ChevronRight, Minus, Plus, RotateCcw } from 'lucide-react';
import styled from 'styled-components';
import type {
    SmartScheduleCountBreakdown,
    VisibleSmartScheduleKind,
} from '@/features/smartSchedule/model';
import Dropdown from '../DropDown/DropDown';
import {
    LEGACY_DAILY_CELL_HEIGHT_DEFAULT,
    LEGACY_DAILY_CELL_HEIGHT_MAX,
    LEGACY_DAILY_CELL_HEIGHT_MIN,
} from './legacyCalendarLayout';
import { startOfLegacyMondayWeek } from './legacyCalendarWeek';
import ScheduleViewSwitcher from './ScheduleViewSwitcher';
import { ScheduleViewMode } from './scheduleViewPreferences';

type Props = {
    selectedDay: any;
    pageType: 'daily' | 'weekly';
    setPageType: Dispatch<SetStateAction<'daily' | 'weekly'>>;
    scheduleCount: number;
    scheduleCountBreakdown: SmartScheduleCountBreakdown;
    scheduleCountByCoach?: Record<string, number>;
    selectedCoachId?: string;
    viewMode?: ScheduleViewMode;
    setViewMode?: (viewMode: ScheduleViewMode) => void;
    onClickSubDate: () => void;
    onClickAddDate: () => void;
    onClickDateToToday: () => void;
    onClickCalendar: () => void;
    coachNameList: any;
    handleSelect?: (selected: any) => void;
    cellHeight?: number;
    onZoomIn?: () => void;
    onZoomOut?: () => void;
};

type ActiveButtonProps = {
    $active: boolean;
};

type LegendMarkProps = {
    $color: string;
    $kind?: 'outline' | 'fill' | 'dot';
};

type ScheduleCountTooltipProps = {
    $expanded: boolean;
};

const SCHEDULE_COUNT_ITEMS: Array<{ kind: VisibleSmartScheduleKind; label: string }> = [
    { kind: 'private', label: 'PT 수업' },
    { kind: 'group', label: '그룹수업' },
    { kind: 'request', label: '신청 대기' },
    { kind: 'general', label: '일반 일정' },
    { kind: 'opportunity', label: '신규기회' },
];

const HeaderShell = styled.header`
    position: fixed;
    z-index: 40;
    top: 76px;
    left: 0;
    width: 100%;
    height: 88px;
    border-bottom: 1px solid #d7e3f7;
    background: rgba(248, 251, 255, 0.96);
    box-shadow: 0 4px 14px rgba(40, 70, 122, 0.06);
    backdrop-filter: blur(10px);
`;

const HeaderRow = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    align-items: center;
    width: 100%;
    height: 100%;
    padding: 0 clamp(24px, 4.44vw, 72px);
    box-sizing: border-box;
    gap: 20px;

    @media (max-width: 1680px) {
        padding: 0 24px;
        gap: 12px;
    }
`;

const LeftControls = styled.div`
    display: flex;
    align-items: center;
    min-width: 0;
    gap: 12px;

    @media (max-width: 1380px) {
        gap: 8px;
    }
`;

const ViewSwitcherSlot = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
`;

const RightControls = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    min-width: 0;
    gap: 10px;

    @media (max-width: 1380px) {
        gap: 8px;
    }
`;

const PeriodSwitcher = styled.div`
    display: inline-flex;
    align-items: center;
    height: 38px;
    padding: 3px;
    box-sizing: border-box;
    border: 1px solid #c9d8f3;
    border-radius: 11px;
    background: #edf4ff;
    flex: 0 0 auto;
`;

const PeriodButton = styled.button<ActiveButtonProps>`
    min-width: 62px;
    height: 30px;
    padding: 0 14px;
    border: 0;
    border-radius: 8px;
    background: ${({ $active }) => ($active ? '#ffffff' : 'transparent')};
    box-shadow: ${({ $active }) => ($active ? '0 2px 7px rgba(49, 91, 166, 0.14)' : 'none')};
    color: ${({ $active }) => ($active ? '#2b62ce' : '#6f83a9')};
    font-size: 14px;
    font-weight: ${({ $active }) => ($active ? 800 : 650)};
    transition: color 150ms ease, background 150ms ease, box-shadow 150ms ease;
    cursor: pointer;

    @media (max-width: 1380px) {
        min-width: 52px;
        padding: 0 10px;
    }
`;

const DateNavigator = styled.div`
    display: inline-flex;
    align-items: center;
    height: 38px;
    padding: 3px;
    box-sizing: border-box;
    border: 1px solid #d8e3f5;
    border-radius: 11px;
    background: #ffffff;
    box-shadow: 0 2px 7px rgba(40, 70, 122, 0.05);
    flex: 0 0 auto;
`;

const DateArrowButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: #6f87b2;
    transition: color 150ms ease, background 150ms ease;
    cursor: pointer;

    &:hover {
        background: #edf4ff;
        color: #3d73dd;
    }
`;

const SelectedDateButton = styled.button`
    min-width: 164px;
    height: 30px;
    padding: 0 12px;
    border: 0;
    background: transparent;
    color: #35425a;
    font-size: 14px;
    font-weight: 750;
    letter-spacing: -0.2px;
    white-space: nowrap;
    cursor: pointer;

    @media (max-width: 1380px) {
        min-width: 145px;
        padding: 0 8px;
    }
`;

const ScheduleCountBadge = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 78px;
    height: 30px;
    padding: 0 11px;
    box-sizing: border-box;
    gap: 6px;
    border-radius: 15px;
    background: #e9f1ff;
    color: #2b529d;
    font-size: 12px;
    font-weight: 800;
    white-space: nowrap;
    flex: 0 0 auto;

    &::before {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: #3d73dd;
        content: '';
    }
`;

const ScheduleCountWrap = styled.span`
    position: relative;
    display: inline-flex;
    flex: 0 0 auto;

    &::after {
        position: absolute;
        top: 100%;
        left: 0;
        width: 100%;
        height: 10px;
        content: '';
    }

    &:hover [role='tooltip'], &:focus-within [role='tooltip'] {
        opacity: 1;
        transform: translate(-50%, 0);
        visibility: visible;
    }
`;

const ScheduleCountButton = styled.button`
    display: inline-flex;
    padding: 0;
    border: 0;
    border-radius: 15px;
    background: transparent;
    cursor: help;

    &:focus-visible {
        outline: 2px solid #7da1e8;
        outline-offset: 2px;
    }
`;

const ScheduleCountTooltip = styled.div<ScheduleCountTooltipProps>`
    position: absolute;
    z-index: 80;
    top: calc(100% + 10px);
    left: 50%;
    width: ${({ $expanded }) => ($expanded ? '220px' : '190px')};
    padding: 12px;
    box-sizing: border-box;
    border: 1px solid #d8e3f5;
    border-radius: 11px;
    background: #ffffff;
    box-shadow: 0 10px 28px rgba(35, 62, 108, 0.18);
    color: #35425a;
    opacity: 0;
    transform: translate(-50%, -4px);
    visibility: hidden;
    pointer-events: auto;
    transition: opacity 140ms ease, transform 140ms ease, visibility 140ms ease;

    &::before {
        position: absolute;
        top: -5px;
        left: calc(50% - 5px);
        width: 9px;
        height: 9px;
        border-top: 1px solid #d8e3f5;
        border-left: 1px solid #d8e3f5;
        background: #ffffff;
        transform: rotate(45deg);
        content: '';
    }
`;

const TooltipTitle = styled.strong`
    display: block;
    margin-bottom: 8px;
    font-size: 12px;
    font-weight: 800;
`;

const TooltipList = styled.span`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const TooltipRow = styled.span`
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: #657188;
    font-size: 11px;
    font-weight: 650;

    b {
        color: #2b529d;
        font-weight: 800;
    }
`;

const TooltipEmpty = styled.span`
    display: block;
    color: #78859a;
    font-size: 11px;
`;

const CoachCountSection = styled.span`
    display: block;
    margin-top: 10px;
    padding-top: 9px;
    border-top: 1px solid #edf1f7;
`;

const CoachCountTitle = styled.strong`
    display: block;
    margin-bottom: 7px;
    color: #53627a;
    font-size: 11px;
    font-weight: 800;
`;

const CoachCountList = styled.span`
    display: flex;
    max-height: 154px;
    padding-right: 3px;
    flex-direction: column;
    gap: 6px;
    overflow-y: auto;

    &::-webkit-scrollbar {
        width: 4px;
    }

    &::-webkit-scrollbar-thumb {
        border-radius: 2px;
        background: #cdd7e7;
    }
`;

const TodayButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 32px;
    padding: 0 11px;
    gap: 5px;
    border: 1px solid transparent;
    border-radius: 9px;
    background: transparent;
    color: #648fe4;
    font-size: 12px;
    font-weight: 750;
    white-space: nowrap;
    transition: border-color 150ms ease, background 150ms ease, color 150ms ease;
    cursor: pointer;

    &:hover {
        border-color: #c9d8f3;
        background: #ffffff;
        color: #3d73dd;
    }

    @media (max-width: 1380px) {
        width: 32px;
        padding: 0;
    }
`;

const TodayLabel = styled.span`
    @media (max-width: 1380px) {
        display: none;
    }
`;

const LegendPanel = styled.div`
    display: inline-flex;
    align-items: center;
    min-height: 38px;
    padding: 0 12px;
    box-sizing: border-box;
    gap: 16px;
    border: 1px solid #d8e3f5;
    border-radius: 11px;
    background: rgba(255, 255, 255, 0.82);
    box-shadow: 0 2px 7px rgba(40, 70, 122, 0.04);
    white-space: nowrap;

    @media (max-width: 1680px) {
        gap: 10px;
        padding: 0 10px;
    }
`;

const LegendItem = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #566276;
    font-size: 11px;
    font-weight: 650;
`;

const LegendLabel = styled.span`
    @media (max-width: 1500px) {
        display: none;
    }
`;

const LegendMark = styled.i<LegendMarkProps>`
    display: inline-block;
    width: ${({ $kind }) => ($kind === 'dot' ? '6px' : '13px')};
    height: ${({ $kind }) => ($kind === 'dot' ? '6px' : '13px')};
    box-sizing: border-box;
    border: ${({ $kind, $color }) => ($kind === 'outline' ? `2px solid ${$color}` : 'none')};
    border-radius: ${({ $kind }) => ($kind === 'dot' ? '50%' : '3px')};
    background: ${({ $kind, $color }) => ($kind === 'outline' ? '#ffffff' : $color)};
    flex: 0 0 auto;
`;

const ZoomControl = styled.div`
    display: inline-flex;
    align-items: center;
    height: 38px;
    padding: 3px;
    box-sizing: border-box;
    border: 1px solid #d8e3f5;
    border-radius: 11px;
    background: #ffffff;
    box-shadow: 0 2px 7px rgba(40, 70, 122, 0.04);
`;

const ZoomButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: #49658f;
    transition: color 150ms ease, background 150ms ease;
    cursor: pointer;

    &:hover:not(:disabled) {
        background: #edf4ff;
        color: #3d73dd;
    }

    &:disabled {
        color: #b7c1d0;
        cursor: not-allowed;
    }
`;

const ZoomValue = styled.span`
    width: 42px;
    color: #53627a;
    font-size: 11px;
    font-weight: 750;
    text-align: center;
`;

const ScheduleTop = ({
    selectedDay,
    pageType,
    setPageType,
    scheduleCount,
    scheduleCountBreakdown,
    scheduleCountByCoach = {},
    selectedCoachId = 'all',
    viewMode = 'calendar',
    setViewMode,
    onClickSubDate,
    onClickAddDate,
    onClickDateToToday,
    onClickCalendar,
    coachNameList,
    handleSelect,
    cellHeight = LEGACY_DAILY_CELL_HEIGHT_DEFAULT,
    onZoomIn,
    onZoomOut,
}: Props) => {
    const scheduleCountTooltipId = useId();
    const baseDate = dayjs(selectedDay);
    const startWeekNumberDate = startOfLegacyMondayWeek(baseDate.startOf('month'));
    const endWeekNumberDate = startOfLegacyMondayWeek(baseDate);
    const weekNumber = Math.floor(endWeekNumberDate.diff(startWeekNumberDate, 'day', true)) / 7 + 1;
    const selectedDateLabel =
        pageType === 'weekly'
            ? `${baseDate.format('YYYY년 MM월')} ${weekNumber}주차`
            : baseDate.format('YYYY년 MM월 DD일(ddd)');
    const visibleScheduleCountItems = SCHEDULE_COUNT_ITEMS.filter(
        ({ kind }) => scheduleCountBreakdown[kind] > 0
    );
    const visibleCoachCountItems = pageType === 'weekly'
        ? (coachNameList || [])
              .filter(
                  (coach: any) =>
                      selectedCoachId === 'all' || String(coach.code) === String(selectedCoachId)
              )
              .map((coach: any) => ({
                  coachId: String(coach.code),
                  coachName: coach.value,
                  count: scheduleCountByCoach[String(coach.code)] || 0,
              }))
        : [];

    return (
        <HeaderShell>
            <HeaderRow>
                <LeftControls>
                    {viewMode === 'calendar' && (
                        <>
                            <PeriodSwitcher role="group" aria-label="캘린더 표시 단위">
                                <PeriodButton
                                    type="button"
                                    $active={pageType === 'daily'}
                                    aria-pressed={pageType === 'daily'}
                                    onClick={() => setPageType('daily')}
                                >
                                    일간
                                </PeriodButton>
                                <PeriodButton
                                    type="button"
                                    $active={pageType === 'weekly'}
                                    aria-pressed={pageType === 'weekly'}
                                    onClick={() => setPageType('weekly')}
                                >
                                    주간
                                </PeriodButton>
                            </PeriodSwitcher>

                            <DateNavigator role="group" aria-label="날짜 이동">
                                <DateArrowButton
                                    type="button"
                                    aria-label={pageType === 'daily' ? '이전 날짜' : '이전 주'}
                                    onClick={onClickSubDate}
                                >
                                    <ChevronLeft size={17} strokeWidth={2.2} aria-hidden="true" />
                                </DateArrowButton>
                                <SelectedDateButton
                                    type="button"
                                    aria-label={`날짜 선택: ${selectedDateLabel}`}
                                    onClick={onClickCalendar}
                                >
                                    {selectedDateLabel}
                                </SelectedDateButton>
                                <DateArrowButton
                                    type="button"
                                    aria-label={pageType === 'daily' ? '다음 날짜' : '다음 주'}
                                    onClick={onClickAddDate}
                                >
                                    <ChevronRight size={17} strokeWidth={2.2} aria-hidden="true" />
                                </DateArrowButton>
                            </DateNavigator>

                            <ScheduleCountWrap>
                                <ScheduleCountButton
                                    type="button"
                                    aria-label={`${scheduleCount}개 일정, 집계 상세`}
                                    aria-describedby={scheduleCountTooltipId}
                                >
                                    <ScheduleCountBadge role="status" aria-live="polite" aria-label={`${scheduleCount}개 일정`}>
                                        {scheduleCount}개 일정
                                    </ScheduleCountBadge>
                                </ScheduleCountButton>
                                <ScheduleCountTooltip
                                    id={scheduleCountTooltipId}
                                    role="tooltip"
                                    $expanded={visibleCoachCountItems.length > 0}
                                >
                                    <TooltipTitle>일정 집계</TooltipTitle>
                                    {visibleScheduleCountItems.length > 0 ? (
                                        <TooltipList>
                                            {visibleScheduleCountItems.map(({ kind, label }) => (
                                                <TooltipRow key={kind}>
                                                    <span>{label}</span>
                                                    <b>{scheduleCountBreakdown[kind]}건</b>
                                                </TooltipRow>
                                            ))}
                                        </TooltipList>
                                    ) : (
                                        <TooltipEmpty>집계된 일정이 없습니다.</TooltipEmpty>
                                    )}
                                    {visibleCoachCountItems.length > 0 && (
                                        <CoachCountSection>
                                            <CoachCountTitle>강사별</CoachCountTitle>
                                            <CoachCountList>
                                                {visibleCoachCountItems.map((coach: any) => (
                                                    <TooltipRow key={coach.coachId}>
                                                        <span>{coach.coachName}</span>
                                                        <b>{coach.count}건</b>
                                                    </TooltipRow>
                                                ))}
                                            </CoachCountList>
                                        </CoachCountSection>
                                    )}
                                </ScheduleCountTooltip>
                            </ScheduleCountWrap>
                            <TodayButton type="button" onClick={onClickDateToToday}>
                                <TodayLabel>오늘</TodayLabel>
                                <RotateCcw size={13} strokeWidth={2.2} aria-hidden="true" />
                            </TodayButton>
                        </>
                    )}
                </LeftControls>

                <ViewSwitcherSlot>
                    {setViewMode && <ScheduleViewSwitcher viewMode={viewMode} onChange={setViewMode} />}
                </ViewSwitcherSlot>

                <RightControls>
                    {coachNameList && coachNameList.length > 0 && pageType === 'weekly' && viewMode === 'calendar' && (
                        <Dropdown
                            options={coachNameList}
                            onSelect={(selected) => handleSelect?.(selected)}
                            bgColor="#FFFFFF"
                            textColor="#3D73DD"
                            borderRadius={10}
                            totalText="모든 강사"
                            arrowColor="#3D73DD"
                            className="h-[38px] py-[7px] border border-[#D8E3F5] shadow-sm"
                            fontSize={13}
                            textType="medium"
                        />
                    )}

                    {viewMode === 'calendar' && (
                        <LegendPanel aria-label="캘린더 범례">
                            <LegendItem aria-label="수업 시간" title="수업 시간">
                                <LegendMark $kind="outline" $color="#3D73DD" /><LegendLabel>수업 시간</LegendLabel>
                            </LegendItem>
                            <LegendItem aria-label="수업 신청 대기" title="수업 신청 대기">
                                <LegendMark $kind="outline" $color="#FF8777" /><LegendLabel>신청 대기</LegendLabel>
                            </LegendItem>
                            <LegendItem aria-label="일반 일정" title="일반 일정">
                                <LegendMark $kind="outline" $color="#6E6E6E" /><LegendLabel>일반 일정</LegendLabel>
                            </LegendItem>
                            {pageType === 'daily' && (
                                <LegendItem aria-label="OFF" title="OFF">
                                    <LegendMark $kind="fill" $color="#D4DBE8" /><LegendLabel>OFF</LegendLabel>
                                </LegendItem>
                            )}
                            <LegendItem aria-label="노쇼" title="노쇼">
                                <LegendMark $kind="dot" $color="#FF3E4A" /><LegendLabel>노쇼</LegendLabel>
                            </LegendItem>
                            <LegendItem aria-label="수업일지 작성" title="수업일지 작성">
                                <LegendMark $kind="dot" $color="#3D73DD" /><LegendLabel>수업일지</LegendLabel>
                            </LegendItem>
                        </LegendPanel>
                    )}

                    {viewMode === 'calendar' && pageType === 'daily' && onZoomIn && onZoomOut && (
                        <ZoomControl role="group" aria-label="캘린더 확대 및 축소">
                            <ZoomButton
                                type="button"
                                aria-label="축소"
                                onClick={onZoomOut}
                                disabled={cellHeight <= LEGACY_DAILY_CELL_HEIGHT_MIN}
                            >
                                <Minus size={15} strokeWidth={2.3} aria-hidden="true" />
                            </ZoomButton>
                            <ZoomValue>{Math.round((cellHeight / LEGACY_DAILY_CELL_HEIGHT_DEFAULT) * 100)}%</ZoomValue>
                            <ZoomButton
                                type="button"
                                aria-label="확대"
                                onClick={onZoomIn}
                                disabled={cellHeight >= LEGACY_DAILY_CELL_HEIGHT_MAX}
                            >
                                <Plus size={15} strokeWidth={2.3} aria-hidden="true" />
                            </ZoomButton>
                        </ZoomControl>
                    )}
                </RightControls>
            </HeaderRow>
        </HeaderShell>
    );
};

export default ScheduleTop;
